import React from 'react';

import {assertAndContinue} from 'Asserts';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {ArchiveRecordGraphQL} from '../../../graphql-fragments/app-core/platform/archivable/__generated/ArchiveRecordGraphQL';
import {ProcessTaskStatus} from '../__generated/ProcessTaskStatus';
import {statusTypeToDisplayName} from '../ProcessConstants';
import StatusSummaryDisplay from './StatusSummaryDisplay';
import {TASK_STATUS_TYPE_WEIGHT} from './TaskStatusUtil';

interface TaskStatusSummaryProps {
  tasks: Array<{status: ProcessTaskStatus}>;
  archiveRecord: ArchiveRecordGraphQL | null;
  includeZeroCountStatuses?: boolean;
}

function ArchivePurposeText(props: {purpose: string}): JSX.Element {
  return <div className="faded u-small-spacing-top u-spacing-bottom">({props.purpose})</div>;
}

function summarizeTasks(
  tasks: Array<{status: ProcessTaskStatus}>,
  includeZeroCountStatuses?: boolean
): Array<{
  statusType: ProcessTaskStatusType;
  count: number;
  label?: string;
}> {
  const countsByStatusType: Array<{
    statusType: ProcessTaskStatusType;
    count: number;
    displayNamesForThisStatus: Set<string>;
  }> = Object.keys(ProcessTaskStatusType)
    .sort(
      (statusType1: ProcessTaskStatusType, statusType2: ProcessTaskStatusType) =>
        TASK_STATUS_TYPE_WEIGHT[statusType1] - TASK_STATUS_TYPE_WEIGHT[statusType2]
    )
    .map((statusType: ProcessTaskStatusType) => ({
      statusType,
      count: 0,
      displayNamesForThisStatus: new Set(),
    }));

  for (const task of tasks) {
    const counterObj = countsByStatusType.find(({statusType}) => statusType === task.status.statusType);
    if (assertAndContinue(counterObj != null, 'Unrecognized status', {status: task.status})) {
      ++counterObj!.count;
      counterObj!.displayNamesForThisStatus.add(task.status.displayName);
    }
  }

  return countsByStatusType
    .filter(({count}) => includeZeroCountStatuses || count > 0)
    .map(({statusType, count, displayNamesForThisStatus}) => ({
      count,
      statusType,
      label:
        displayNamesForThisStatus.size === 1
          ? displayNamesForThisStatus.values().next().value
          : statusTypeToDisplayName(statusType),
    }));
}

/**
 * Summarizes the status of the tasks for this process. E.g.: "3 Done  2 In process" (with icons!).
 * If the process has been archived, shows the archive purpose instead of the task status summary.
 *
 * Note that we're very particular about the text we use to describe the status:
 *   - If all tasks of the given statusType have the same displayLabel, we use that.
 *   - Otherwise, we use the stringified form of the statusType.
 */
export default function TaskStatusSummary(props: TaskStatusSummaryProps): JSX.Element {
  if (props.archiveRecord != null) {
    return <ArchivePurposeText purpose={props.archiveRecord.purpose} />;
  }

  return (
    <div className="u-small-spacing-top u-spacing-bottom ">
      <StatusSummaryDisplay statusCounts={summarizeTasks(props.tasks, props.includeZeroCountStatuses)} />
    </div>
  );
}
